import logoCakeColor from "@/assets/logo/promptlayer-cake-color.svg";
import logoCake from "@/assets/logo/promptlayer-cake.svg";
import logoLong from "@/assets/logo/promptlayer-long.png";
import { OrganizationSelector } from "@/components/OrganizationSelector";
import Refresh from "@/components/SidebarSearchLayout/components/RefreshButton";
import Settings from "@/components/SidebarSearchLayout/components/Settings";
import SubscriptionStatusBanner from "@/components/SubscriptionStatusBanner";
import { WorkspaceSelector } from "@/components/WorkspaceSelector";
import { IS_ORGANIZATIONS_ENABLED } from "@/constants";
import { useUser } from "@/context/user-context";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";
import { NavigationMenu } from "./NavigationMenu";

const Header = ({
  sidebarCollapsed,
  setSidebarCollapsed,
  requestsOn,
}: {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: Dispatch<SetStateAction<boolean>>;
  requestsOn: boolean;
}) => {
  const navigate = useNavigate();

  return (
    <div className="bg-blue absolute hidden w-full sm:block">
      <div className="flex items-center">
        <div
          className={`flex flex-shrink-0 items-center px-3 text-gray-700 transition-all duration-100 ease-in-out ${
            sidebarCollapsed ? "w-20" : "w-80"
          }`}
        >
          {sidebarCollapsed ? (
            <img
              src={logoCake}
              alt="PromptLayer Logo"
              className="h-10 w-10 cursor-pointer transition duration-300 ease-in-out hover:animate-spin hover:text-gray-600"
              onClick={() => navigate("/")}
              onMouseOver={(e) => (e.currentTarget.src = logoCakeColor)}
              onMouseOut={(e) => (e.currentTarget.src = logoCake)}
            />
          ) : (
            <div className="flex-1 text-xl font-bold tracking-wide">
              <span
                className="cursor-pointer hover:text-gray-600"
                onClick={() => navigate("/")}
              >
                <img
                  src={logoLong}
                  alt="PromptLayer Logo"
                  width={180}
                  className="transition duration-100 ease-in-out"
                />
              </span>
            </div>
          )}
          <button
            className="mr-1 rounded-md p-2 hover:bg-gray-200 focus:outline-none"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? (
              <ChevronRightIcon className="h-5 w-5" />
            ) : (
              <ChevronLeftIcon className="h-5 w-5" />
            )}
          </button>
          <Refresh
            isSidebarCollapsed={sidebarCollapsed}
            requestsOn={requestsOn}
          />
        </div>
        <Navbar />
      </div>
    </div>
  );
};

const Navbar = () => {
  const userContext = useUser();
  const subscriptionStatus = userContext.subscriptionStatus;
  const isFreePlan = userContext.userIsFreePlan;

  const { sessionId } = useParams();

  const [defaultPlaygroundSessionId, setDefaultPlaygroundSessionId] =
    useState<string>(sessionId ?? v4());

  useEffect(() => {
    if (
      window.location.pathname.includes(
        `playground/${defaultPlaygroundSessionId}`,
      )
    )
      return;
    setDefaultPlaygroundSessionId(v4());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  const [showPricingModal, setShowPricingModal] = useState(false);

  return (
    <nav className="flex w-full items-center px-8 lg:py-4" aria-label="Global">
      <div className="flex flex-1 space-x-4 2xl:space-x-8">
        <Selectors />
        <NavigationMenu />
      </div>
      <div className="flex items-center gap-4">
        <Settings />
        {isFreePlan && (
          <SubscriptionStatusBanner
            subscriptionStatus={subscriptionStatus.data}
            subscriptionStatusisLoading={subscriptionStatus.isLoading}
            showPricingModal={showPricingModal}
            setShowPricingModal={setShowPricingModal}
          />
        )}
      </div>
    </nav>
  );
};

export default Header;

const Selectors = () => {
  if (!IS_ORGANIZATIONS_ENABLED) return <WorkspaceSelector />;
  return (
    <div className="flex items-center gap-2">
      <OrganizationSelector />
      <div>/</div>
      <WorkspaceSelector />
    </div>
  );
};
